/**
 * Utility functions for handling confidence scores and descriptions
 */

/**
 * Converts a confidence score to a descriptive text based on the isScam decision
 * @param {number} confidence - A number between 0 and 1 representing confidence in the decision
 * @param {boolean} isScam - Whether the analysis determined this is a scam
 * @returns {string} Descriptive text based on confidence range and scam determination
 */
export const getConfidenceDescription = (confidence, isScam = null) => {
  if (confidence === null || confidence === undefined || isNaN(confidence)) {
    return "Unknown confidence";
  }

  const score = Math.max(0, Math.min(1, confidence)); // Clamp between 0 and 1

  // Debug logging to help identify the issue
  console.log(
    "getConfidenceDescription - confidence:",
    confidence,
    "isScam:",
    isScam,
    "typeof isScam:",
    typeof isScam
  );

  // If isScam is not provided (null, undefined, or not a boolean), fall back to old behavior
  if (isScam === null || isScam === undefined || typeof isScam !== "boolean") {
    console.log("Falling back to old behavior - isScam is not a valid boolean");
    if (score >= 0 && score <= 0.125) {
      return "Not a scam";
    } else if (score >= 0.126 && score <= 0.25) {
      return "Very unlikely a scam";
    } else if (score >= 0.251 && score <= 0.375) {
      return "Unlikely a scam";
    } else if (score >= 0.376 && score <= 0.5) {
      return "Possibly a scam";
    } else if (score >= 0.501 && score <= 0.625) {
      return "Likely a scam";
    } else if (score >= 0.626 && score <= 0.75) {
      return "Very likely a scam";
    } else if (score >= 0.751 && score <= 0.875) {
      return "Almost certainly a scam";
    } else if (score >= 0.876 && score <= 1.0) {
      return "Definitely a scam";
    }
    return "Unknown likelihood of being a scam";
  }

  // New correct behavior: confidence represents certainty in the decision
  console.log("Using new behavior - isScam is a valid boolean:", isScam);
  const resultType = isScam ? "a scam" : "legitimate";

  if (score >= 0.9) {
    return `Almost certainly ${resultType}`;
  } else if (score >= 0.8) {
    return `Very likely ${resultType}`;
  } else if (score >= 0.7) {
    return `Likely ${resultType}`;
  } else if (score >= 0.6) {
    return `Probably ${resultType}`;
  } else if (score >= 0.5) {
    return `Possibly ${resultType}`;
  } else if (score >= 0.4) {
    return `Uncertain if ${resultType}`;
  } else if (score >= 0.3) {
    return `Unlikely ${resultType}`;
  } else if (score >= 0.2) {
    return `Very unlikely ${resultType}`;
  } else {
    return `Uncertain assessment (${
      isScam ? "flagged as scam" : "marked as legitimate"
    } but low confidence)`;
  }
};

/**
 * Gets the confidence score as a percentage string
 * @param {number} confidence - A number between 0 and 1
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Percentage string (e.g., "85.5%")
 */
export const getConfidencePercentage = (confidence, decimals = 1) => {
  if (confidence === null || confidence === undefined || isNaN(confidence)) {
    return "0.0%";
  }

  const score = Math.max(0, Math.min(1, confidence)); // Clamp between 0 and 1
  return (score * 100).toFixed(decimals) + "%";
};

/**
 * Gets the CSS class for confidence styling based on the score
 * @param {number} confidence - A number between 0 and 1
 * @returns {string} CSS class name
 */
export const getConfidenceStyleClass = (confidence) => {
  if (confidence === null || confidence === undefined || isNaN(confidence)) {
    return "confidence-unknown";
  }

  const score = Math.max(0, Math.min(1, confidence)); // Clamp between 0 and 1

  if (score <= 0.25) {
    return "confidence-very-low";
  } else if (score <= 0.5) {
    return "confidence-low";
  } else if (score <= 0.75) {
    return "confidence-medium";
  } else {
    return "confidence-high";
  }
};
